import { Component, Input, Output, EventEmitter, ViewChild, Inject, ChangeDetectionStrategy } from '@angular/core';
import { MatDatepicker, MatDatepickerInputEvent } from '@angular/material/datepicker';
import { DatePeriodType } from '../../search/query-context';
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { FORMATS_DASH_NUMBER } from '../utils/format-data-picker';
import { LanguageService } from 'app/core/language.service';
import TimeUtils from '../utils/time-utils';

@Component({
  selector: 'app-date-range-picker',
  templateUrl: './date-range-picker.component.html',
  styleUrls: ['./date-range-picker.component.scss'],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'sv_SE' },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: FORMATS_DASH_NUMBER },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DateRangePickerComponent {
  @ViewChild('startDatePicker', { static: true })
  startDatePicker: MatDatepicker<Date>;
  @ViewChild('endDatePicker', { static: true })
  endDatePicker: MatDatepicker<Date>;

  @Output()
  public startDate: EventEmitter<DateChangedEvent> = new EventEmitter<DateChangedEvent>();

  @Output()
  public endDate: EventEmitter<DateChangedEvent> = new EventEmitter<DateChangedEvent>();

  @Input()
  @Output()
  public initialStartDate: Date;
  @Input()
  @Output()
  public initialEndDate: Date;

  @Input()
  set minDate(val: Date | undefined) {
    if (val) {
      this._minDate = val;
    }
  }
  get minDate(): Date {
    return this._minDate;
  }
  private _minDate: Date = new Date(2017, 3, 1);
  public maxDate: Date;
  public datePeriods: DatePeriod[] = [
    new DatePeriod(DatePeriodType.LastSevenDays, 7, 'latest7days'),
    new DatePeriod(DatePeriodType.LastTwoWeeks, 14, 'latest2weeks'),
    new DatePeriod(DatePeriodType.LastThirtyDays, 30, 'latest1month'),
    new DatePeriod(DatePeriodType.LastNintyDays, 90, 'latest3months'),
    new DatePeriod(DatePeriodType.LastYear, 365, 'latest1year'),
  ];

  constructor(
    private _adapter: DateAdapter<any>,
    private languageService: LanguageService,
    @Inject(MAT_DATE_LOCALE) private _locale: string,
  ) {
    this.maxDate = new Date();
    this.setLanguage();
  }

  onStartDateSelectedChanged(event: MatDatepickerInputEvent<Date>) {
    this.startDate.emit(new DateChangedEvent(event.value));
  }

  onEndDateSelectedChanged(event: MatDatepickerInputEvent<Date>) {
    this.endDate.emit(new DateChangedEvent(event.value));
  }

  onStartDateSelectedClicked() {
    this.startDatePicker.open();
  }

  onEndDateSelectedClicked() {
    this.endDatePicker.open();
  }

  onPredefinedDateSlidingClicked(datePeriod: DatePeriod) {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - datePeriod.daysBack);
    this.startDate.emit(new DateChangedEvent(startDate, datePeriod.type));
  }

  public setLanguage() {
    this._locale = this.languageService.getLocaleFromCurrentLanguage().toString();
    this._adapter.setLocale(this._locale);
  }
}

export class DatePeriod {
  public type: DatePeriodType;
  public daysBack: number;
  public translationKey: string;

  constructor(type: DatePeriodType, daysBack: number, translationKey: string) {
    this.type = type;
    this.daysBack = daysBack;
    this.translationKey = translationKey;
  }
}

export class DateChangedEvent {
  date: Date;
  period: DatePeriodType;

  constructor(date: Date, period?: DatePeriodType) {
    this.date = TimeUtils.getDateToDate(date);
    this.period = period;
  }
}
